<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("claims") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="page"
				/>
			</template>
			<template slot="actions">
				<j-button v-can="'writeClaims'" :to="{ name: 'ClaimCreate' }">
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create_claim") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<!-- <div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_claims_number')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
				>
					{{ $t("cancel") }}
				</j-button>
			</div>
		</div>-->

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Actions -->
							<template v-if="column.field === 'actions'">
								<!-- Delete -->
								<crm-icon
									name="delete"
									size="normal"
									color="cError"
									@click.native="showModal(item.id)"
									v-can="'writeClaims'"
								/>
							</template>

							<template v-else-if="column.field === 'issuerNumber'">
								<router-link
									:to="{
										name: `ClaimDetail`,
										params: { id: item.id }
									}"
								>
									{{ item.issuerNumber ? item.issuerNumber : "---" }}
								</router-link>
							</template>

							<!-- Name -->
							<template v-else-if="column.field === 'name'">
								<router-link v-if="item.claimant" :to="item.claimant.to">
									{{ item.claimant.name }}
								</router-link>
							</template>

							<!-- Policy -->
							<template v-else-if="column.field === 'policy'">
								<router-link v-if="item.policy" :to="item.policy.to">
									{{ item.policy.issuerNumber }}
								</router-link>
							</template>

							<!-- Status -->
							<template v-else-if="column.field === 'status'">
								<j-tag
									:variant="
										(status =>
											({
												PROCESSING: 'warning',
												APPROVED: 'success',
												REJECTED: 'error'
											}[status]))(item.status)
									"
									:label="$t(item.status)"
								/>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems"
				:perPage="itemsPerPage"
				:activePage="page"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="removeClaim(selectedItemId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_claim") }}</j-dialog
		>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JTag from "@/components/JTag"
import JDataTable from "@/components/JDataTable/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDialog from "@/components/JDialog/"
import dayjs from "dayjs"
import api from "../api/"
import get from "lodash.get"
import debounce from "lodash.debounce"

const getClaimantFromItem = item =>
	item?.claimant
		? {
				id: item.claimant.id,
				name: item.claimant.name,
				to: {
					name: `ClientDetail`,
					params: {
						clientType: "individual",
						id: item.claimant.id
					}
				}
		  }
		: null

const getPolicyFromItem = item =>
	item.policy?.id
		? {
				issuerNumber: item.policy.issuerNumber
					? `#${item.policy.issuerNumber}`
					: null,
				to: {
					name: `PolicyDetail`,
					params: {
						id: item.policy.id
					}
				}
		  }
		: null

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JPagination,
		JTag,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JDialog
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			page: 1,
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "issuerNumber",
					text: this.$t("claim_number")
				},
				{
					text: `${this.$t("policy")}#`,
					field: "policy"
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("status"),
					field: "status"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			],

			selectedItemId: null
		}
	},

	computed: {
		computedList() {
			return this.listing.list.map(item => ({
				...item,
				issuerNumber: item.issuerNumber,
				claimant: getClaimantFromItem(item),
				policy: getPolicyFromItem(item),
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.page = pageIndex
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value = null) {
			this.searchTerm = value === null || value === "" ? null : value.trim()
			this.page = 1
			this.readAll()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.page = 1
			this.onSearch()
		},

		async readAll() {
			this.isLoading = true
			const { itemsPerPage } = this
			const sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.page - 1) * itemsPerPage
			const limit = itemsPerPage

			// Api call
			this.isLoading = true

			// Success
			const res = await api.listClaims({ skip, limit, sort })

			this.isLoading = false

			if (res.errors) this.$store.dispatch("showError", res.errors)

			this.listing.list = get(res, "data.claims.list")
			this.listing.numberOfItems = get(res, "data.claims.totalCount")
		},

		showModal(id) {
			this.selectedItemId = id
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItemId = null
			this.$modal.hide("j-dialog")
		},

		//TODO: Only delete when no product/policy attached to productType
		async removeClaim(id) {
			const res = await api.deleteClaim({ claimId: id })

			this.hideModal()

			if (res.errors) this.$store.dispatch("showError", res.errors)
			else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("claim_deleted")
					}
				})

			this.readAll()
		}
	},

	created() {
		this.readAll()
	}
}
</script>
